'use client';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ExtendTextContent } from '@//components/ExtendTextContent';
import { InlineButton } from '@//components/InlineButton';
import { Skeleton } from '@//components/Skeleton';
import { TextButton } from '@//components/TextButton';

interface ExtendTextContentContainerProps {
  children: React.ReactNode;
  numberOfLines?: string;
  buttonType?: 'inlineButton' | 'textButton';
}

export const ExtendTextContentContainer: React.FC<ExtendTextContentContainerProps> = ({
  children,
  numberOfLines,
  buttonType = 'textButton',
}) => {
  const tComponents = useTranslations('components.extendTextContent');
  const tCommon = useTranslations('other.common');
  const [showMore, setShowMore] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkTextTruncation = useCallback(() => {
    if (contentRef.current && !showMore) {
      const element = contentRef.current;
      const isOverflowing = element.scrollHeight > element.clientHeight;
      setIsTextTruncated(isOverflowing);
      setIsChecking(false);
    }
  }, [showMore]);

  useEffect(() => {
    checkTextTruncation();

    const resizeObserver = new ResizeObserver(() => {
      checkTextTruncation();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      if (contentRef.current) {
        resizeObserver.unobserve(contentRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [checkTextTruncation, children]);

  const handleManualToggle = useCallback(() => {
    setShowMore(!showMore);
  }, [showMore]);

  const handleContentFocus = useCallback(
    (_event: React.FocusEvent<HTMLDivElement>) => {
      if (!showMore) {
        setShowMore(true);
      }
    },
    [showMore],
  );

  return (
    <ExtendTextContent
      ref={contentRef}
      isExtended={showMore}
      numberOfLines={numberOfLines}
      onFocus={handleContentFocus}
      actionSpacing={buttonType === 'inlineButton' ? 'dense' : 'default'}
      action={
        isChecking ? (
          buttonType === 'inlineButton' ? (
            <Skeleton width="5rem" height="1.5rem" />
          ) : (
            <Skeleton width="6rem" height="1.5rem" />
          )
        ) : (
          (isTextTruncated || showMore) &&
          (buttonType === 'inlineButton' ? (
            <InlineButton
              onClick={handleManualToggle}
              title={showMore ? tComponents('showLessTitle') : tComponents('showMoreTitle')}
              size="md"
            >
              {showMore ? tCommon('readLess') : tCommon('readMore')}
            </InlineButton>
          ) : (
            <TextButton
              onClick={handleManualToggle}
              icon={
                showMore ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />
              }
              title={showMore ? tComponents('showLessTitle') : tComponents('showMoreTitle')}
            >
              {showMore ? tCommon('readLess') : tCommon('readMore')}
            </TextButton>
          ))
        )
      }
    >
      {children}
    </ExtendTextContent>
  );
};
